<template>
  <div class="game-list-slider">
    <div class="slider-container"
         @touchstart="handleTouchStart"
         @touchmove="handleTouchMove"
         @touchend="handleTouchEnd"
         @mousedown="handleMouseDown"
         @mousemove="handleMouseMove"
         @mouseup="handleMouseUp"
         @mouseleave="handleMouseUp">
      <div class="slider-wrapper" :style="{ transform: `translateY(-${currentBlock * 100}%)` }">
        <div v-for="(block, index) in gameBlocks" :key="index" class="slide-block">
          <div class="game-grid">
            <game-card
              v-for="game in block"
              :key="game.id"
              :game="game"
              :image-url="imageUrl"
              :query-string="queryString"
            />
          </div>
        </div>
      </div>
      <div class="slider-nav">
        <button
          class="nav-btn prev-btn"
          @click="prevBlock"
          :disabled="currentBlock === 0"
        >
          <i class="fas fa-chevron-up"></i>
        </button>
        <div class="nav-dots">
          <button
            v-for="(_, index) in gameBlocks"
            :key="index"
            class="nav-dot"
            :class="{ active: currentBlock === index }"
            @click="currentBlock = index"
          />
        </div>
        <button
          class="nav-btn next-btn"
          @click="nextBlock"
          :disabled="currentBlock === gameBlocks.length - 1"
        >
          <i class="fas fa-chevron-down"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import GameCard from './game-card-mobile.vue'

export default {
  name: 'GameListSlider',
  components: {
    GameCard
  },
  props: {
    games: {
      type: Array,
      required: true
    },
    imageUrl: {
      type: String,
      required: true
    },
    queryString: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentBlock: 0,
      gamesPerBlock: 6, /* 2列 × 3行 = 6個遊戲 */
      touchStartY: 0,
      touchEndY: 0,
      mouseDown: false,
      mouseStartY: 0,
      mouseEndY: 0
    }
  },
  computed: {
    gameBlocks() {
      const blocks = []
      for (let i = 0; i < this.games.length; i += this.gamesPerBlock)
        blocks.push(this.games.slice(i, i + this.gamesPerBlock))

      return blocks
    }
  },
  methods: {
    prevBlock() {
      if (this.currentBlock > 0) this.currentBlock--
    },
    nextBlock() {
      if (this.currentBlock < this.gameBlocks.length - 1) this.currentBlock++
    },
    handleTouchStart(e) {
      this.touchStartY = e.touches[0].clientY
    },
    handleTouchMove(e) {
      this.touchEndY = e.touches[0].clientY
    },
    handleTouchEnd() {
      const deltaY = this.touchEndY - this.touchStartY
      if (Math.abs(deltaY) > 50) {
        if (deltaY < 0) this.nextBlock()
        else this.prevBlock()
      }
    },
    handleMouseDown(e) {
      this.mouseDown = true
      this.mouseStartY = e.clientY
    },
    handleMouseMove(e) {
      if (!this.mouseDown) return
      this.mouseEndY = e.clientY
    },
    handleMouseUp() {
      if (!this.mouseDown) return
      const deltaY = this.mouseEndY - this.mouseStartY
      if (Math.abs(deltaY) > 50) {
        if (deltaY < 0) this.nextBlock()
        else this.prevBlock()
      }
      this.mouseDown = false
      this.mouseStartY = 0
      this.mouseEndY = 0
    }
  }
}
</script>

<style lang="scss" scoped>
.game-list-slider {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 10px 20px 10px;

  .slider-container {
    position: relative;
    height: 800px;
    overflow: hidden;
  }

  .slider-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    transition: transform 0.5s ease;
  }

  .slide-block {
    flex: 0 0 100%;
    height: 100%;
    padding: 15px;
  }

  .game-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 1fr);
    gap: 32px 24px;
    height: 100%;
    padding: 0;
  }

  .game-card {
    /* 恢復原始樣式 */
  }

  .slider-nav {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    z-index: 10;
  }

  .nav-btn {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.2);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    i {
      font-size: 18px;
    }
  }

  .nav-dots {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .nav-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }

    &.active {
      background: #007bff;
    }
  }
}

@media (max-width: 768px) {
  .game-list-slider {
    padding: 0 5px 10px 5px;

    .slider-container {
      height: 900px;
    }

    .game-grid {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(6, 1fr);
      gap: 4px;
      padding: 0;
    }

    .game-card {
      /* 恢復原始樣式 */
    }

    .nav-btn {
      width: 35px;
      height: 35px;
    }
  }
}

@media (max-width: 480px) {
  .game-list-slider {
    padding: 0 2px 5px 2px;

    .slider-container {
      height: 800px;
    }

    .game-grid {
      gap: 3px;
      padding: 0;
    }

    .nav-btn {
      width: 30px;
      height: 30px;

      i {
        font-size: 14px;
      }
    }

    .nav-dot {
      width: 6px;
      height: 6px;
    }
  }
}
</style>